import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

const API = process.env.REACT_APP_BACKEND_URL;
const digits = (value) => String(value || "").replace(/\D/g, "");
const probableLandline = (value) => digits(value).replace(/^39/, "").startsWith("0");

export default function WhatsAppSchedaLeadDialog({ open, onOpenChange, lead, structure, mode = "scheda", headers, onSent }) {
  const contacts = useMemo(() => (structure?.contatti || []).filter((c) => c?.telefono), [structure]);
  const [to, setTo] = useState("");
  const [hideName, setHideName] = useState(false);
  const [hideEmail, setHideEmail] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTo(contacts[0]?.telefono || structure?.telefono || "");
    setHideName(false);
    setHideEmail(true);
  }, [open, structure, contacts]);

  const preview = async () => {
    try {
      const url = `${API}/api/admin/leads/${lead.id}/scheda-pdf?nascondi_nome=${hideName}&nascondi_email=${hideEmail}`;
      const res = await fetch(url, { headers: { Authorization: headers.Authorization } });
      if (!res.ok) throw new Error("Impossibile creare l'anteprima");
      const blobUrl = URL.createObjectURL(await res.blob());
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) { toast.error(error.message); }
  };

  const send = async () => {
    if (!to.trim()) return toast.error("Indica il numero WhatsApp della struttura.");
    setSending(true);
    try {
      const suffix = mode === "posti" ? "richiesta-posti" : "scheda-lead";
      const body = mode === "posti" ? { to: to.trim() } : { to: to.trim(), hide_name: hideName, hide_email: hideEmail };
      const res = await fetch(`${API}/api/admin/leads/${lead.id}/whatsapp/strutture/${structure.struttura_id}/${suffix}`, {
        method: "POST", headers, body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Invio WhatsApp non riuscito");
      toast.success(mode === "posti" ? "Richiesta disponibilità inviata" : "Scheda lead inviata");
      onOpenChange(false);
      onSent?.();
    } catch (error) { toast.error(error.message); } finally { setSending(false); }
  };

  const titolo = mode === "posti" ? "Chiedi posti disponibili" : "Invia scheda lead su WhatsApp";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{titolo} — {structure?.nome}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-slate-600">{mode === "posti" ? "Invierai il messaggio preimpostato con le risposte rapide." : "Il PDF viene inviato con il template Meta approvato."}</p>
          <div><Label>Destinatario WhatsApp *</Label>
            {contacts.length ? <Select value={to} onValueChange={setTo}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{contacts.map((contact) => <SelectItem key={`${contact.ruolo}-${contact.telefono}`} value={contact.telefono}>{contact.ruolo}{contact.nome ? ` — ${contact.nome}` : ""}: {contact.telefono}{probableLandline(contact.telefono) ? " (fisso: verifica WhatsApp)" : ""}</SelectItem>)}</SelectContent></Select>
              : <Input value={to} onChange={(event) => setTo(event.target.value)} placeholder="+39…" />}
          </div>
          {mode === "scheda" && <><div className="flex items-center gap-2"><Checkbox id="wa-hide-name" checked={hideName} onCheckedChange={setHideName} /><Label htmlFor="wa-hide-name">Nascondi il nome del lead nel PDF</Label></div><div className="flex items-center gap-2"><Checkbox id="wa-hide-email" checked={hideEmail} onCheckedChange={setHideEmail} /><Label htmlFor="wa-hide-email">Nascondi email nel PDF</Label></div><Button size="sm" variant="outline" onClick={preview}>Anteprima PDF</Button></>}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button><Button onClick={send} disabled={sending}>{sending ? "Invio…" : mode === "posti" ? "Invia richiesta" : "Invia scheda"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
